import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface SourceRow {
  readonly searchSourceId: string;
  readonly sourceCode: string;
  readonly sourceName: string;
  readonly sourceModule: string;
  readonly sourceType: string;
  readonly crawlEndpoint: string | null;
  readonly defaultClassification: string;
  readonly priority: number;
  readonly isActive: boolean;
  readonly lastSyncedAt: string | null;
}

const SOURCE_TYPES = ['MODULE', 'EXTERNAL', 'FILE_STORE', 'DATABASE'];
const CLASSIFICATIONS = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'];

/**
 * M39 — Federated Search-Source Registry Console (GR_M36-M40 §L39.02 / §M row 15).
 * The admin surface over the federated search-source master (T522B): search
 * administrators register the modules / systems the universal search indexes, each
 * with its crawl_endpoint, exposed entity types, default classification and sync
 * cadence, so the source list is DB-driven (LAW 6) rather than hard-coded. Viewing
 * needs SOURCE_VIEW; authoring / deleting / marking-synced needs SOURCE_MANAGE
 * (SoD). Soft-delete only (LAW 3); tenant-isolated server-side (LAW 4). This screen
 * backs the /admin/search/sources route.
 */
export function SearchSourcesConsole(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<SourceRow[]>([]);
  const [moduleFilter, setModuleFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [sourceCode, setSourceCode] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [sourceModule, setSourceModule] = useState('');
  const [sourceType, setSourceType] = useState('MODULE');
  const [crawlEndpoint, setCrawlEndpoint] = useState('');
  const [entityTypes, setEntityTypes] = useState('');
  const [defaultClassification, setDefaultClassification] = useState('INTERNAL');
  const [syncIntervalMinutes, setSyncIntervalMinutes] = useState('60');
  const [priority, setPriority] = useState('100');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (moduleFilter) params.set('sourceModule', moduleFilter);
    apiRequest<{ data: SourceRow[] }>(`/v1/search/sources?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, moduleFilter, reload]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    setCreated(null);
    if (!sourceCode.trim() || !sourceName.trim() || !sourceModule.trim()) {
      setFormError(t('m39.sources.requiredFields'));
      return;
    }
    setBusy(true);
    try {
      const entityTypeList = entityTypes
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part.length > 0);
      await apiRequest<{ data: SourceRow }>('/v1/search/sources', {
        method: 'POST',
        body: {
          sourceCode: sourceCode.trim(),
          sourceName: sourceName.trim(),
          sourceModule: sourceModule.trim(),
          sourceType,
          crawlEndpoint: crawlEndpoint.trim() || undefined,
          entityTypes: entityTypeList.length > 0 ? entityTypeList : undefined,
          defaultClassification,
          syncIntervalMinutes: Number(syncIntervalMinutes) || 60,
          priority: Number(priority) || 100,
          description: description.trim() || undefined,
        },
        token,
      });
      setCreated(t('m39.sources.created', { code: sourceCode.trim() }));
      setSourceCode(''); setSourceName(''); setSourceModule(''); setCrawlEndpoint('');
      setEntityTypes(''); setDescription(''); setSyncIntervalMinutes('60'); setPriority('100');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setFormError(t('m39.sources.notAuthorised'));
      } else {
        setFormError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const markSynced = async (r: SourceRow): Promise<void> => {
    try {
      await apiRequest(`/v1/search/sources/${r.searchSourceId}/mark-synced`, { method: 'POST', token });
      setReload((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const toggleActive = async (r: SourceRow): Promise<void> => {
    try {
      await apiRequest(`/v1/search/sources/${r.searchSourceId}`, {
        method: 'PATCH',
        body: { isActive: !r.isActive },
        token,
      });
      setReload((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const remove = async (r: SourceRow): Promise<void> => {
    try {
      await apiRequest(`/v1/search/sources/${r.searchSourceId}`, { method: 'DELETE', token });
      setReload((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <main aria-label="search-sources-console">
      <h1>{t('m39.sources.title')}</h1>
      <p>{t('m39.sources.intro')}</p>

      <section aria-label="source-create">
        <h2>{t('m39.sources.registerSource')}</h2>
        <form onSubmit={submit} aria-label="source-form">
          <label>{t('m39.sources.sourceCode')}
            <input aria-label="source-code" value={sourceCode} onChange={(e) => setSourceCode(e.target.value)} />
          </label>
          <label>{t('m39.sources.sourceName')}
            <input aria-label="source-name" value={sourceName} onChange={(e) => setSourceName(e.target.value)} />
          </label>
          <label>{t('m39.common.sourceModule')}
            <input aria-label="source-module" value={sourceModule} onChange={(e) => setSourceModule(e.target.value)} />
          </label>
          <label>{t('m39.sources.sourceType')}
            <select aria-label="source-type" value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
              {SOURCE_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m39.sources.crawlEndpoint')}
            <input aria-label="crawl-endpoint" value={crawlEndpoint} onChange={(e) => setCrawlEndpoint(e.target.value)} />
          </label>
          <label>{t('m39.sources.entityTypes')}
            <input aria-label="entity-types" value={entityTypes} onChange={(e) => setEntityTypes(e.target.value)} />
          </label>
          <label>{t('m39.sources.defaultClassification')}
            <select aria-label="classification" value={defaultClassification} onChange={(e) => setDefaultClassification(e.target.value)}>
              {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m39.sources.syncInterval')}
            <input aria-label="sync-interval" type="number" value={syncIntervalMinutes} onChange={(e) => setSyncIntervalMinutes(e.target.value)} />
          </label>
          <label>{t('m39.common.priority')}
            <input aria-label="priority" type="number" value={priority} onChange={(e) => setPriority(e.target.value)} />
          </label>
          <label>{t('m39.common.description')}
            <input aria-label="description" value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <Button variant="primary" type="submit" disabled={busy}>{busy ? t('m39.common.saving') : t('m39.sources.registerSource')}</Button>
        </form>
        {formError && <p role="alert">{formError}</p>}
        {created && <p aria-label="create-success">{created}</p>}
      </section>

      <section aria-label="source-filters">
        <label>{t('m39.common.filterByModule')}
          <input aria-label="filter-module" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)} />
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading
        ? <p>{t('common.loading')}</p>
        : (
          <DataTable<SourceRow>
            ariaLabel="source-table"
            testId="source-table"
            rowKey={(r) => r.searchSourceId}
            rowAriaLabel={(r) => `source-${r.sourceCode}`}
            emptyMessage={t('m39.sources.empty')}
            rows={rows}
            columns={[
              { key: 'code', header: t('m39.common.code'), render: (r) => r.sourceCode },
              { key: 'name', header: t('m39.common.name'), render: (r) => r.sourceName },
              { key: 'module', header: t('m39.common.module'), render: (r) => r.sourceModule },
              { key: 'type', header: t('m39.common.type'), render: (r) => r.sourceType },
              { key: 'endpoint', header: t('m39.sources.colEndpoint'), render: (r) => r.crawlEndpoint ?? '—' },
              { key: 'class', header: t('m39.sources.colClass'), render: (r) => r.defaultClassification },
              { key: 'priority', header: t('m39.common.priority'), render: (r) => r.priority },
              { key: 'lastSynced', header: t('m39.sources.colLastSynced'), render: (r) => r.lastSyncedAt ? at(r.lastSyncedAt) : t('m39.sources.never') },
              { key: 'active', header: t('m39.common.active'), render: (r) => r.isActive ? t('common.yes') : t('common.no') },
              { key: 'actions', header: t('m39.common.actions'), render: (r) => (
                <>
                  <Button variant="secondary" type="button" aria-label={`sync-${r.sourceCode}`} onClick={() => markSynced(r)}>{t('m39.sources.markSynced')}</Button>
                  <Button variant="secondary" type="button" aria-label={`toggle-${r.sourceCode}`} onClick={() => toggleActive(r)}>
                    {r.isActive ? t('m39.common.disable') : t('m39.common.enable')}
                  </Button>
                  <Button variant="danger" type="button" aria-label={`delete-${r.sourceCode}`} onClick={() => remove(r)}>{t('common.delete')}</Button>
                </>
              ) },
            ]}
          />
        )}
    </main>
  );
}
